import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import { useTransactionContext } from '../../../components/TransactionProvider';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import MuiLink from '@material-ui/core/Link';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import Property from '@afosto/app-components-react/Property';
import sortByDate from '@afosto/utils/date/sortByDate';
import formatCurrency from '@afosto/utils/formatCurrency';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import PaymentCard from '../../../components/PaymentCard/PaymentCard';
import {
  issuerFormatter,
  methodFormatter,
  providerFormatter,
  actionsFormatter,
} from './formatters';
import { boolean as booleanFormatter } from '@afosto/app-components-react/DataGrid/formatters';
import translations from './translations';
import ReferenceFormatter from '../../Transactions/components/ReferenceFormatter';

const TRANSACTION_APP_URL = 'https://transactions.afosto.io';

const paymentsContainer = props => <List {...props} />;

const TransactionDetails = () => {
  const intl = useIntl();
  const { transaction } = useTransactionContext();
  const { client_id: clientId } = transaction ?? {};
  const [client, setClient] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  const dataSource = useDataGrid(sortByDate(transaction?.payments ?? [], 'created_at'), {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'amount',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.amount',
            defaultMessage: 'Amount',
            description: 'The column name for column amount',
          }),
          type: 'currency',
        },
        {
          key: 'provider_code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.providerCode',
            defaultMessage: 'Provider',
            description: 'The column name for column provider code',
          }),
          type: 'provider',
        },
        {
          key: 'method_code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.methodCode',
            defaultMessage: 'Method',
            description: 'The column name for column method code',
          }),
          type: 'method',
        },
        {
          key: 'issuer_code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.issuerCode',
            defaultMessage: 'Issuer',
            description: 'The column name for column issuer code',
          }),
          type: 'issuer',
        },
        {
          key: 'is_refund',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.isRefund',
            defaultMessage: 'Refund',
            description: 'The column name for column is refund',
          }),
          type: 'boolean',
        },
        {
          key: 'is_paid',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.isPaid',
            defaultMessage: 'Paid',
            description: 'The column name for column is paid',
          }),
          type: 'boolean',
        },
        {
          key: 'paid_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.paidAt',
            defaultMessage: 'Paid at',
            description: 'The column name for column paid at',
          }),
          type: 'datetime',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.payments.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
        {
          key: 'actions',
          name: '',
          type: 'actions',
        },
      ],
    },
  });

  useEffect(() => {
    let isMounted = true;

    const fetchClient = async id => {
      try {
        const response = await oauthApi.getClient(id);
        const { data = null } = response;

        if (isMounted) {
          setClient(data);
        }
      } catch (err) {
        if (isMounted) {
          setClient(null);
        }
      }
    };

    if (clientId) {
      fetchClient(clientId);
    }

    return () => {
      isMounted = false;
    };
  }, [clientId]);

  return (
    <Grid container spacing={2} direction={isMobile ? 'column-reverse' : 'row'}>
      <Grid item xs={12} lg={9} xl={9}>
        <Card>
          <CardHeader
            title={intl.formatMessage(translations.payments.title)}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Hidden implementation="js" mdUp>
            <DataList
              dataSource={dataSource}
              renderItem={payment => <PaymentCard payment={payment} key={payment.id} />}
              component={paymentsContainer}
            />
          </Hidden>
          <Hidden implementation="js" smDown>
            <DataGrid
              dataSource={dataSource}
              formatters={{
                issuer: issuerFormatter,
                method: methodFormatter,
                provider: providerFormatter,
                actions: actionsFormatter,
              }}
            />
          </Hidden>
        </Card>
      </Grid>
      <Grid item xs={12} lg={3} xl={3}>
        <Card>
          <CardHeader
            title={intl.formatMessage(translations.details.title)}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <CardContent>
            <Property
              label={intl.formatMessage(translations.details.type)}
              value={intl.formatMessage({
                id: `properties.${transaction?.reference?.entity_type}`,
                defaultMessage: transaction?.reference?.entity_type ?? '-',
                description: 'value of the type field',
              })}
            />

            <Property
              label={intl.formatMessage(translations.details.description)}
              value={transaction?.description}
            />

            <Property
              label={intl.formatMessage(translations.details.reference)}
              value={
                <ReferenceFormatter
                  typographyVariant="body2"
                  type={transaction?.reference?.entity_type}
                  id={transaction?.reference?.entity_id}
                />
              }
            />

            <Property
              label={intl.formatMessage(translations.details.amountDue)}
              value={
                transaction
                  ? formatCurrency(transaction.amount_due / 100, transaction?.currency)
                  : undefined
              }
            />

            <Property
              label={intl.formatMessage(translations.details.amountPaid)}
              value={
                transaction
                  ? formatCurrency(transaction?.amount_paid / 100, transaction?.currency)
                  : undefined
              }
            />

            <Property
              label={intl.formatMessage(translations.details.isPaid)}
              value={booleanFormatter(transaction?.is_paid)}
            />

            {transaction?.amount_due >= 0 && (
              <Property
                label={intl.formatMessage(translations.details.paymentUrl)}
                value={
                  transaction?.secret ? (
                    <MuiLink
                      variant="body2"
                      href={`${TRANSACTION_APP_URL}/${transaction?.secret}`}
                      target="_blank"
                    >
                      {`${TRANSACTION_APP_URL}/${transaction?.secret}`}
                    </MuiLink>
                  ) : (
                    '-'
                  )
                }
                valueTypographyProps={{
                  color: 'textPrimary',
                  variant: 'body2',
                  style: { wordBreak: 'break-all' },
                }}
              />
            )}

            <Property
              label={intl.formatMessage(translations.details.client)}
              value={client?.name}
            />

            <Property
              label={intl.formatMessage(translations.details.createdAt)}
              value={
                transaction?.created_at
                  ? formatDateTime(new Date(transaction?.created_at))
                  : undefined
              }
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default TransactionDetails;
